import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';

// Splash keys for extensibility
export const SPLASH_KEYS = {
  INTRO_UPGRADE: 'intro_upgrade',
  CERTIFICATION_TAB_CLICK: 'certification_tab_click',
  PROGRESS_MILESTONE: 'progress_milestone',
  ANSWER_SPLASH: 'answer_splash',
} as const;

type SplashKey = (typeof SPLASH_KEYS)[keyof typeof SPLASH_KEYS];

interface SplashContextType {
  activeSplash: SplashKey | null;
  requestIntroSplash: (hotelId: string) => void;
  requestCertificationSplash: () => void;
  requestProgressMilestoneSplash: (hotelId: string) => void;
  requestAnswerSplash: () => void;
  closeSplash: () => void;
  clearQueue: () => void;
}

const SplashContext = createContext<SplashContextType | undefined>(undefined);

// Storage key prefix
const STORAGE_PREFIX = 'alo_splash_';

/**
 * Get the storage key for a specific splash, hotel, and login session
 */
const getSplashStorageKey = (
  loginSessionId: string,
  hotelId: string,
  splashKey: SplashKey
): string => {
  return `${STORAGE_PREFIX}${loginSessionId}_${hotelId}_${splashKey}`;
};

/**
 * Check if a splash has been shown for a specific hotel in the current login session
 */
const hasSplashBeenShown = (
  loginSessionId: string,
  hotelId: string,
  splashKey: SplashKey
): boolean => {
  const key = getSplashStorageKey(loginSessionId, hotelId, splashKey);
  return localStorage.getItem(key) === 'shown';
};

/**
 * Mark a splash as shown for a specific hotel in the current login session
 */
const markSplashAsShown = (
  loginSessionId: string,
  hotelId: string,
  splashKey: SplashKey
): void => {
  const key = getSplashStorageKey(loginSessionId, hotelId, splashKey);
  localStorage.setItem(key, 'shown');
};

/**
 * Clear all splash data for a specific login session
 */
const clearSplashDataForSession = (loginSessionId: string): void => {
  const keys = Object.keys(localStorage);
  const sessionPrefix = `${STORAGE_PREFIX}${loginSessionId}_`;

  keys.forEach(key => {
    if (key.startsWith(sessionPrefix)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Clear all splash data (all sessions)
 */
export const clearAllSplashData = (): void => {
  const keys = Object.keys(localStorage);

  keys.forEach(key => {
    if (key.startsWith(STORAGE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

export const SplashProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { token, isSuperAdmin } = useAuth();
  const [activeSplash, setActiveSplash] = useState<SplashKey | null>(null);
  const [splashQueue, setSplashQueue] = useState<SplashKey[]>([]);
  const [currentHotelId, setCurrentHotelId] = useState<string>('');

  // Use aloAuthToken as loginSessionId
  const loginSessionId = token || '';

  /**
   * Check if user is a Boost subscriber
   * TODO: Implement actual Boost check when user-level subscription is available
   * For now, returns false to allow splash to show for testing
   */
  const isBoostUser = useCallback((): boolean => {
    // TODO: Check user subscription when backend provides user-level subscription
    // Example: return user?.subscription?.code === 'BOOST';
    return false;
  }, []);

  /**
   * Add a splash to the queue if not already present
   */
  const addToQueue = useCallback((splashKey: SplashKey) => {
    setSplashQueue(currentQueue => {
      // Don't add if already in queue
      if (currentQueue.includes(splashKey)) {
        return currentQueue;
      }
      return [...currentQueue, splashKey];
    });
  }, []);

  /**
   * Process the next splash in queue
   */
  const processQueue = useCallback(() => {
    if (activeSplash !== null) return; // Don't process if splash is active

    setSplashQueue(currentQueue => {
      if (currentQueue.length === 0) return currentQueue;

      const [nextSplash, ...remainingQueue] = currentQueue;
      setActiveSplash(nextSplash);
      return remainingQueue;
    });
  }, [activeSplash]);

  /**
   * Request to show the intro/upgrade splash
   * Priority: High (shows immediately or queues first)
   * Only shows if:
   * - User is not Boost
   * - Splash hasn't been shown for this hotel in this session
   */
  const requestIntroSplash = useCallback(
    (hotelId: string) => {
      if (isSuperAdmin) return;
      if (!loginSessionId || !hotelId) return;

      // Don't show to Boost users
      if (isBoostUser()) return;

      // Check if already shown for this hotel in this session
      if (
        hasSplashBeenShown(loginSessionId, hotelId, SPLASH_KEYS.INTRO_UPGRADE)
      ) {
        return;
      }

      if (activeSplash === SPLASH_KEYS.INTRO_UPGRADE) {
        return;
      }

      // Store the hotelId for closing
      setCurrentHotelId(hotelId);

      // If no splash is active, show immediately
      if (activeSplash === null) {
        setActiveSplash(SPLASH_KEYS.INTRO_UPGRADE);
      } else {
        // Queue it with high priority (add to front)
        setSplashQueue(currentQueue => {
          if (currentQueue.includes(SPLASH_KEYS.INTRO_UPGRADE)) {
            return currentQueue;
          }
          return [SPLASH_KEYS.INTRO_UPGRADE, ...currentQueue];
        });
      }
    },
    [loginSessionId, isBoostUser, activeSplash, isSuperAdmin]
  );

  /**
   * Request to show the certification splash
   * Priority: Low (always queues, never interrupts)
   * Shows EVERY TIME (not persisted) - user can see it multiple times
   */
  const requestCertificationSplash = useCallback(() => {
    if (isSuperAdmin) return;

    // Certification splash shows every time, no persistence check
    // Always queue certification splash (low priority)
    if (activeSplash === SPLASH_KEYS.CERTIFICATION_TAB_CLICK) {
      return;
    }
    if (activeSplash === null) {
      setActiveSplash(SPLASH_KEYS.CERTIFICATION_TAB_CLICK);
    } else {
      addToQueue(SPLASH_KEYS.CERTIFICATION_TAB_CLICK);
    }
  }, [activeSplash, addToQueue, isSuperAdmin]);

  /**
   * Request to show the progress milestone splash
   * Priority: Medium (queues but with preference over certification)
   * Only shows once per hotel per session
   */
  const requestProgressMilestoneSplash = useCallback(
    (hotelId: string) => {
      if (isSuperAdmin) return;
      if (!loginSessionId || !hotelId) return;

      // Check if already shown for this hotel in this session
      if (
        hasSplashBeenShown(
          loginSessionId,
          hotelId,
          SPLASH_KEYS.PROGRESS_MILESTONE
        )
      ) {
        return;
      }
      if (activeSplash === SPLASH_KEYS.PROGRESS_MILESTONE) {
        return;
      }

      // Store the hotelId for closing
      setCurrentHotelId(hotelId);

      // If no splash is active, show immediately
      if (activeSplash === null) {
        setActiveSplash(SPLASH_KEYS.PROGRESS_MILESTONE);
      } else {
        // Queue it with medium priority (before certification, after intro)
        setSplashQueue(currentQueue => {
          if (currentQueue.includes(SPLASH_KEYS.PROGRESS_MILESTONE)) {
            return currentQueue;
          }
          // Insert before certification splash if it exists
          const certIndex = currentQueue.indexOf(
            SPLASH_KEYS.CERTIFICATION_TAB_CLICK
          );
          if (certIndex !== -1) {
            const newQueue = [...currentQueue];
            newQueue.splice(certIndex, 0, SPLASH_KEYS.PROGRESS_MILESTONE);
            return newQueue;
          }
          return [...currentQueue, SPLASH_KEYS.PROGRESS_MILESTONE];
        });
      }
    },
    [loginSessionId, activeSplash, isSuperAdmin]
  );

  // show it everytime when user selects 'yes' for certification question
  const requestAnswerSplash = useCallback(
    () => {
      if (isSuperAdmin) return;
      if (!loginSessionId) return;  
      if (activeSplash === SPLASH_KEYS.ANSWER_SPLASH) {
        return;
      }
      // If no splash is active, show immediately
      if (activeSplash === null) {
        setActiveSplash(SPLASH_KEYS.ANSWER_SPLASH);
      } else {
        // Queue it with medium priority (before certification, after intro)
        setSplashQueue(currentQueue => {
          if (currentQueue.includes(SPLASH_KEYS.ANSWER_SPLASH)) {
            return currentQueue;
          }
          // Insert before certification splash if it exists
          const certIndex = currentQueue.indexOf(
            SPLASH_KEYS.CERTIFICATION_TAB_CLICK
          );
          if (certIndex !== -1) {
            const newQueue = [...currentQueue];
            newQueue.splice(certIndex, 0, SPLASH_KEYS.ANSWER_SPLASH);
            return newQueue;
          }
          return [...currentQueue, SPLASH_KEYS.ANSWER_SPLASH];
        });
      }
    },
    [loginSessionId, activeSplash, isSuperAdmin]
  );

  /**
   * Close the currently active splash and process queue
   */
  const closeSplash = useCallback(() => {
    if (activeSplash) {
      // Only mark as shown if it's not certification splash (certification shows every time)
      // and we have both loginSessionId and hotelId
      if (
        activeSplash !== SPLASH_KEYS.CERTIFICATION_TAB_CLICK &&
        loginSessionId &&
        currentHotelId
      ) {
        markSplashAsShown(loginSessionId, currentHotelId, activeSplash);
      }
      setActiveSplash(null);
    }
  }, [activeSplash, loginSessionId, currentHotelId]);

  /**
   * Clear the splash queue (e.g., when navigating away from hotel detail page)
   */
  const clearQueue = useCallback(() => {
    setSplashQueue([]);
  }, []);

  /**
   * Process queue when active splash is closed
   */
  useEffect(() => {
    if (activeSplash === null && splashQueue.length > 0) {
      // Small delay to ensure smooth transition between splashes
      const timer = setTimeout(() => {
        processQueue();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeSplash, splashQueue.length, processQueue]);

  /**
   * Clear splash data when login session changes (logout/new login)
   */
  useEffect(() => {
    // When token changes, it's a new session or logout
    // Clear splash state and queue
    setActiveSplash(null);
    setSplashQueue([]);
  }, [token]);

  return (
    <SplashContext.Provider
      value={{
        activeSplash,
        requestIntroSplash,
        requestCertificationSplash,
        requestProgressMilestoneSplash,
        requestAnswerSplash,
        closeSplash,
        clearQueue,
      }}
    >
      {children}
    </SplashContext.Provider>
  );
};

export const useSplash = () => {
  const context = useContext(SplashContext);
  if (context === undefined) {
    throw new Error('useSplash must be used within a SplashProvider');
  }
  return context;
};

/**
 * Hook to clear splash data on logout
 * Should be called from logout handlers
 */
export const useClearSplashOnLogout = (loginSessionId: string | null) => {
  useEffect(() => {
    return () => {
      if (loginSessionId) {
        clearSplashDataForSession(loginSessionId);
      }
    };
  }, [loginSessionId]);
};
